import React, { useEffect, useMemo } from 'react'
import { useGetAudienceConnectorsByAudienceId } from '../../../../../api/dealership/customers/audiences/useGetAudienceConnectorsByAudienceId'
import { useNavigate, useParams } from 'react-router'
import { useGetAudienceMetricsByAudienceId } from '../../../../../api/dealership/customers/audiences/useGetAudienceMetricsByAudienceId'
import dayjs from 'dayjs'
import AudienceFlowDiagram, {
  useAudienceFlowDiagramState,
} from '../../../../../components/Audiences/AudienceDetails/AudienceFlowDiagram'
import create from 'zustand'
import AudienceMetricCard from '../../../../../components/Audiences/AudienceDetails/AudienceMetricCard'
import { useGetAudienceById } from '../../../../../api/dealership/customers/audiences/useGetAudienceById'
import { ReactComponent as IconCalendar } from './../../../../../images/icons/icon_calendar.svg'
import { ReactComponent as AudiencePeople } from './../../../../../images/icons/audience_people.svg'
import { ReactComponent as ActionEdit } from './../../../../../images/icons/action_edit.svg'
import LineChart from '../../../../../components/Charts/LineChart'
import { useGetAudienceUpdatesTrendsByAudienceId } from '../../../../../api/dealership/customers/audiences/useGetAudienceUpdatesTrendsByAudienceId'
import {
  Button,
  ChevronLeft,
  DateRangePicker,
  PageHeader,
  Pill,
  StatsCard,
} from '@demandlocal/components'
import { ReactComponent as SalesDataIcon } from './../../../../../images/icons/sales_data_icon.svg'
import { useModal } from '../../../../../components/useModal'
import { Route, Routes, useSearchParams } from 'react-router-dom'
import { DateRange } from '../../../../../lib/types/DateRange'
import GoogleVendorAudienceDetails from '../../../../../components/Audiences/AudienceDetails/GoogleVendorAudienceDetails'
import { VENDOR } from '../../../../../lib/utils/vendors'
import TradeDeskVendorAudienceDetails from '../../../../../components/Audiences/AudienceDetails/TradeDeskVendorAudienceDetails'
import Modal from '../../../../../components/Modal/Modal'
import SoldCustomers from '../../../../../forms/AddCustomers/Routes/SoldCustomers'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import EditAudience from '../../../../../forms/EditAudience/EditAudience'
import { formatNumber } from '../../../../../lib/utils/formatNumber'
import { match } from 'ts-pattern'
type AudienceMetricsState = {
  filters: {
    dateRange: DateRange
    audience_id?: string
    vendor_account_id?: string
    vendor_campaign_id?: string
  }
  setFilters: (
    filters: (prev: AudienceMetricsState['filters']) => AudienceMetricsState['filters'],
  ) => void
  setVendorAccountId: (vendorAccountId: string) => void
}

export const useAudienceMetricsState = create<AudienceMetricsState>((set) => ({
  filters: {
    dateRange: {
      dateRange: {
        startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
        endDate: dayjs().startOf('day').format('YYYY-MM-DD'),
      },
    },
  },
  setVendorAccountId: (vendor_account_id?: string) => {
    set((state: AudienceMetricsState) => ({
      filters: {
        ...state.filters,
        vendor_account_id,
      },
    }))
  },
  setFilters: (
    fn: (prev: AudienceMetricsState['filters']) => AudienceMetricsState['filters'],
  ) => set((state) => ({ filters: fn(state.filters) })),
}))

const AudienceDetails = (): JSX.Element => {
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const { audience_id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const campaign_id = searchParams.get('campaign_id')
  const { setSelectedNodeByProperty } = useAudienceFlowDiagramState()
  const { filters, setFilters } = useAudienceMetricsState()
  const { data: audience } = useGetAudienceById(audience_id)
  const { data: connectors, isLoading } =
    useGetAudienceConnectorsByAudienceId(audience_id)
  const { handleModal } = useModal()

  useEffect(() => {
    if (audience_id) {
      setFilters((prev: AudienceMetricsState['filters']) => ({
        ...prev,
        audience_id,
      }))
    }
  }, [audience_id, setFilters])

  useEffect(() => {
    if (campaign_id && !isLoading) setSelectedNodeByProperty('id', campaign_id)
  }, [campaign_id, isLoading, setSelectedNodeByProperty])

  const { data: metrics, isLoading: isLoadingMetrics } =
    useGetAudienceMetricsByAudienceId({
      audience_id: filters.audience_id,
      start_date: filters.dateRange?.dateRange.startDate,
      end_date: filters.dateRange?.dateRange.endDate,
      vendor_account_id: filters.vendor_account_id,
      vendor_campaign_id: filters.vendor_campaign_id,
    })
  const {
    data: updatesTrends,
    isLoading: isLoadingUpdatesTrends,
    isPreviousData,
  } = useGetAudienceUpdatesTrendsByAudienceId({
    audience_id: filters.audience_id,
    start_date: filters.dateRange?.dateRange.startDate,
    end_date: filters.dateRange?.dateRange.endDate,
  })

  const { selectedNode } = useAudienceFlowDiagramState()

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev) => ({
      ...prev,
      dateRange: dateRange,
    }))
  }

  const emptyCharts = useMemo(() => {
    return !isLoadingMetrics && (!metrics || !metrics.length)
  }, [isLoadingMetrics, metrics])

  const audienceIncreasePercentage: {
    value: string
    type: 'success' | 'error' | 'warning'
  } = useMemo(() => {
    if (updatesTrends?.length) {
      const last = updatesTrends[updatesTrends.length - 1]
      const first = updatesTrends[0]
      const trend = last.customersSize - first.customersSize || 0
      if (trend > 0) {
        return {
          value: `+${((trend / first.customersSize) * 100).toFixed(2)}%`,
          type: 'success',
        }
      } else if (trend < 0) {
        return {
          value: `${((trend / first.customersSize) * 100).toFixed(2)}%`,
          type: 'error',
        }
      }
      return {
        value: `0%`,
        type: 'warning',
      }
    }
    return {
      value: '0%',
      type: 'success',
    }
  }, [updatesTrends])

  const VendorAudience = match(selectedNode)
    .with({ data: { vendor_id: VENDOR.Google } }, (value: any) => (
      <a
        rel="noreferrer"
        href={`https://ads.google.com/aw/audiences/management/details?ocid=${selectedNode?.data.vendor_account_id}&userListId=${selectedNode?.data.vendor_audience_id}`}
        target="_blank"
        className={
          'shadow-md rounded-full border-primary-xiketic border-2 px-5 py-3 font-bold bg-primary-xiketic text-white white-icon'
        }>
        {value?.data?.name}
      </a>
    ))
    .with({ data: { vendor_id: VENDOR.Facebook } }, (value: any) => (
      <a
        rel="noreferrer"
        href={`https://adsmanager.facebook.com/adsmanager/audiences?act=${selectedNode?.data?.vendor_account_id?.replace(
          'act_',
          '',
        )}`}
        target="_blank"
        className={
          'shadow-md rounded-full border-primary-xiketic border-2 px-5 py-3 font-bold bg-primary-xiketic text-white white-icon'
        }>
        {value?.data?.name}
      </a>
    ))
    .otherwise((value) => (
      <div
        className={
          'shadow-md rounded-full border-primary-xiketic border-2 px-5 py-3 font-bold bg-primary-xiketic text-white'
        }>
        {value?.data?.name}
      </div>
    ))

  return (
    <div className={'flex flex-col gap-8 p-8 font-Spartan'}>
      <div className="flex flex-row items-center justify-between">
        <div className="flex gap-3">
          <ChevronLeft
            // @ts-ignore
            className="fill-web-gray cursor-pointer"
            onClick={(e) => navigate('..')}
          />
          <span className="font-bold text-2xl">{audience?.audience_name}</span>
        </div>
        <Button
          onClick={() => {
            handleModal(<EditAudience audience_id={audience?._id} />)
          }}
          /* @ts-ignore */
          rightSlot={<ActionEdit className={'stroke-white'} />}>
          Edit Audience
        </Button>
      </div>
      <div className="flex flex-row gap-3">
        <StatsCard
          title="Total Sources"
          content={<span>{formatNumber(connectors?.inputs?.length ?? 0)}</span>}
        />
        <StatsCard
          title="Audience Size"
          content={<span>{formatNumber(audience?.audience_size ?? 0)}</span>}
        />
        <StatsCard
          title="Linked Destinations"
          content={<span>{formatNumber(connectors?.outputs?.length ?? 0)}</span>}
        />
      </div>
      <div className={'h-[280px] pt-10'}>
        <AudienceFlowDiagram connectors={connectors} isLoading={isLoading} />
      </div>
      <div className={'flex justify-between items-center'}>
        <div className={'flex items-center gap-4'}>
          <div className={'text-base font-bold text-primary-xiketic'}>Showing</div>
          <div className={'text-base font-bold text-primary-xiketic'}>{'>'}</div>
          {VendorAudience}
        </div>
        <div className={'flex items-center font-Spartan'}>
          <DateRangePicker
            filters={{ dateRange: filters.dateRange.dateRange }}
            onChange={onDateRangeChange}
          />
        </div>
      </div>
      <div className={'grid grid-cols-2 gap-5'}>
        <AudienceMetricCard
          title={'Start date of audience'}
          value={dayjs(audience?.createdAt).format('MM/DD/YYYY')}>
          <AudienceMetricCard.Icon>
            <div className={'p-3 bg-primary-persian-blue/5 rounded-full'}>
              <IconCalendar className={'stroke-primary-persian-blue'} />
            </div>
          </AudienceMetricCard.Icon>
        </AudienceMetricCard>
        <AudienceMetricCard
          title={'Size'}
          value={new Intl.NumberFormat('en-US').format(audience?.audience_size ?? 0)}>
          <AudienceMetricCard.Icon>
            <div className={'p-3 bg-primary-persian-blue/5 rounded-full'}>
              <AudiencePeople className={'stroke-primary-persian-blue'} />
            </div>
          </AudienceMetricCard.Icon>
        </AudienceMetricCard>
      </div>
      {selectedNode?.data?.vendor_id === VENDOR.Google && (
        <GoogleVendorAudienceDetails
          audience_id={audience?._id}
          vendor_id={VENDOR.Google}
        />
      )}
      {selectedNode?.data.vendor_id === VENDOR.TradeDesk && (
        <TradeDeskVendorAudienceDetails
          audience_id={audience?._id}
          vendor_id={VENDOR.TradeDesk}
        />
      )}
      <div className={'flex grid grid-cols-2 gap-5'}>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span className={classNames('', { 'opacity-40': emptyCharts })}>
              Total Impressions
            </span>
            <span className={classNames('', { 'opacity-40': emptyCharts })}>
              {new Intl.NumberFormat('en-US').format(
                metrics
                  ?.map((metric: { impressions: number }) => metric.impressions)
                  .reduce((acc: number, cur: number) => acc + cur, 0),
              )}
            </span>
          </div>
          <div className={'h-[270px] p-2'}>
            <LineChart
              data={metrics ?? []}
              gradient={'green-to-cyan'}
              yKey={'impressions'}
              isLoading={isLoadingMetrics}
              isPreviousData={isPreviousData}
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span className={classNames('', { 'opacity-40': emptyCharts })}>
              Total Clicks
            </span>
            <span className={classNames('', { 'opacity-40': emptyCharts })}>
              {new Intl.NumberFormat('en-US').format(
                metrics
                  ?.map(
                    (metric: { impressions: number; clicks: number }) => metric.clicks,
                  )
                  .reduce((acc: number, cur: number) => acc + cur, 0),
              )}
            </span>
          </div>
          <div className={'h-[270px] p-2'}>
            <LineChart
              data={metrics ?? []}
              gradient={'red-to-orange'}
              yKey={'clicks'}
              isLoading={isLoadingMetrics}
              isPreviousData={isPreviousData}
            />
          </div>
        </div>
      </div>
      <div
        className={
          'p-5 flex items-center justify-between border-2 rounded-lg border-dashed'
        }>
        <div className={'flex items-center gap-5'}>
          <SalesDataIcon />
          <div className={'flex flex-col gap-1'}>
            <span className={'font-bold'}>You have no sales data yet</span>
            <span>Add some</span>
          </div>
        </div>
        <div>
          <Button
            variant={'secondary'}
            leftSlot={<div>+</div>}
            onClick={(e) => {
              e.preventDefault()
              navigate(
                `${t('dealership.customers.sources.create', { dealership })}/sales`,
              )
            }}>
            Add sales data
          </Button>
        </div>
      </div>
      <div className={'pb-5 border-2 rounded-lg flex flex-col gap-3'}>
        <div
          className={
            'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
          }>
          <span>Audience size trend</span>
          <div className={'flex items-center gap-2'}>
            {new Intl.NumberFormat('en-US').format(audience?.audience_size ?? 0)}
            <Pill type={audienceIncreasePercentage?.type}>
              {audienceIncreasePercentage?.value}
            </Pill>
          </div>
        </div>
        <div className={'w-full h-[320px] p-2'}>
          <LineChart
            data={updatesTrends ?? []}
            gradient={'green-to-lemon-green'}
            yKey={'customersSize'}
            isLoading={isLoadingUpdatesTrends}
            isPreviousData={isPreviousData}
            borderColor={'#0A9652'}
          />
        </div>
      </div>
      <div
        className={
          'p-5 border-2 rounded-lg flex flex-col gap-3 min-h-[240px] text-primary-xiketic'
        }>
        <div className={'text-primary-xiketic text-sm font-bold opacity-40'}>Filters</div>
        <div className={'w-full h-full flex items-center justify-center opacity-40'}>
          No filters
        </div>
      </div>
      <Routes>
        <Route
          path={'customers/new/sales'}
          element={
            <Modal modalRoute={true} isOpen={true} key={'AddAudienceSoldCustomers'}>
              <SoldCustomers />
            </Modal>
          }
        />
      </Routes>
    </div>
  )
}

export default AudienceDetails
